import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Loader, AlertCircle } from 'lucide-react';
import { useLeviersCiblage } from './leviersServis';
import SyntheseCiblage from './SyntheseCiblage';
import ParcoursProspect from './ParcoursProspect';
import JournalObservations from './JournalObservations';
import ModaleObservation from './ModaleObservation';
import VerdictLargeurCiblage from './VerdictLargeurCiblage';
import NosLeviers from './NosLeviers';

/**
 * L'onglet Ciblage : comment un concurrent construit ses audiences.
 *
 * ## Les deux moitiés, et pourquoi elles restent séparées
 *
 * - **Le ciblage publié** (`VerdictLargeurCiblage`) vient de la bibliothèque publicitaire :
 *   c'est ce que l'annonceur a **déclaré** à Meta au titre du DSA, et cela ne dit que la
 *   largeur de l'audience.
 * - **Le journal** vient du panneau « Pourquoi je vois cette pub ? » : c'est ce qui a
 *   **déclenché** une diffusion, et c'est la seule source de cette information.
 *
 * ⚠️ Les deux ne prouvent pas la même chose, et l'écran ne les additionne pas. Un verdict
 * « audience ouverte » est une *présomption* ; un levier « confirmé » est un relevé croisé. Les
 * mêler ferait passer une obligation réglementaire pour une observation de terrain.
 *
 * ## ⚠️ Visible en lecture seule, contrairement à l'onglet Insights
 *
 * Insights est masqué aux accès clients parce que rien n'y est conservé en base : sans
 * génération possible, l'onglet n'aurait jamais rien à montrer. Ici les relevés **sont**
 * persistés : un client a quelque chose à lire, et le masquer lui cacherait un travail fait
 * pour lui. Seules les affordances d'écriture disparaissent, et chaque fonction d'écriture sort
 * tôt — il faut les deux barrières.
 */
const OngletCiblage = ({ concurrent, token, readOnly = false }) => {
  const entetes = { headers: { Authorization: `Bearer ${token}` } };

  const {
    referentiel, chargement: chargementRef, erreur: erreurRef,
    familles, gestes, seuils, libelleSource, libelleGeste
  } = useLeviersCiblage(token);

  const [synthese, setSynthese] = useState(null);
  const [parcours, setParcours] = useState([]);
  const [observations, setObservations] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [tri, setTri] = useState({ sortBy: 'observeLe', sortOrder: 'desc' });
  const [levier, setLevier] = useState('');
  const [saisieAutorisee, setSaisieAutorisee] = useState(false);
  const [creas, setCreas] = useState([]);
  const [nos, setNos] = useState(null);

  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [modale, setModale] = useState(null); // null | { observation }

  const chargerSynthese = useCallback(async () => {
    const { data } = await axios.get(`/api/competitors/${concurrent.id}/ciblage/synthese`, entetes);
    setSynthese(data.data);
    // ⚠️ Le serveur tranche en dernier, mais l'écran doit le savoir avant d'afficher un
    // formulaire : un 403 en bandeau rouge se lit comme une panne alors que rien n'est cassé.
    setSaisieAutorisee(data.saisieAutorisee && !readOnly);
  }, [concurrent.id, token, readOnly]);

  const chargerParcours = useCallback(async () => {
    const { data } = await axios.get(`/api/competitors/${concurrent.id}/parcours`, entetes);
    setParcours(data.data || []);
  }, [concurrent.id, token]);

  const chargerJournal = useCallback(async () => {
    const params = new URLSearchParams({
      page: String(page), limit: '25', sortBy: tri.sortBy, sortOrder: tri.sortOrder
    });
    if (levier) params.set('levier', levier);
    const { data } = await axios.get(`/api/competitors/${concurrent.id}/observations?${params}`, entetes);
    setObservations(data.data || []);
    setTotal(data.total || 0);
    setTotalPages(data.totalPages || 1);
  }, [concurrent.id, token, page, tri, levier]);

  /**
   * Nos propres leviers.
   *
   * ⚠️ **Chargé à part, et sans bloquer le reste.** Cette lecture sort sur le réseau Meta — deux
   * appels par compte rattaché — et peut échouer. L'adosser au chargement principal ferait
   * attendre le journal, qui est en base et instantané, derrière une lecture distante qui ne le
   * concerne pas. Un échec laisse donc `nos` à `null` : le panneau ne paraît pas, et les deux
   * autres moitiés de l'onglet restent lisibles.
   */
  const chargerNos = useCallback(async () => {
    try {
      const { data } = await axios.get(`/api/competitors/${concurrent.id}/ciblage/nos-audiences`, entetes);
      setNos(data.data);
    } catch {
      setNos(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [concurrent.id, token]);

  /** Les créas proposées à la saisie. Le plafond est volontaire : une liste déroulante de deux
   *  cents entrées ne se parcourt pas, et l'observateur a `creaLibelle` pour le reste. */
  const chargerCreas = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `/api/competitors/${concurrent.id}/ads?limit=60&sortBy=lastSeen&status=active`, entetes);
      setCreas(data.data || []);
    } catch { setCreas([]); }
  }, [concurrent.id, token]);

  useEffect(() => {
    let vivant = true;
    (async () => {
      setChargement(true);
      setErreur(null);
      try {
        await Promise.all([chargerSynthese(), chargerParcours(), chargerJournal(), chargerCreas()]);
        // Après l'essentiel, et sans l'attendre : le réseau Meta ne doit pas retarder l'écran.
        chargerNos();
      } catch (e) {
        if (vivant) setErreur(e.response?.data?.error || "Le ciblage n'a pas pu être chargé.");
      } finally {
        if (vivant) setChargement(false);
      }
    })();
    return () => { vivant = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [concurrent.id]);

  // Le journal seul se recharge au changement de page, de tri ou de filtre : recharger la
  // synthèse à chaque clic d'en-tête coûterait une lecture complète pour rien.
  useEffect(() => {
    if (chargement) return;
    chargerJournal().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, tri, levier]);

  /** Après une écriture, les trois lectures se refont : la synthèse bouge avec le journal. */
  const rafraichir = useCallback(async () => {
    await Promise.all([chargerSynthese(), chargerParcours(), chargerJournal()]);
  }, [chargerSynthese, chargerParcours, chargerJournal]);

  const supprimer = useCallback(async (id) => {
    if (!saisieAutorisee) return;
    try {
      await axios.delete(`/api/competitors/${concurrent.id}/observations/${id}`, entetes);
      await rafraichir();
    } catch (e) {
      setErreur("La suppression a échoué.");
    }
  }, [concurrent.id, token, saisieAutorisee, rafraichir]);

  if (chargementRef || chargement) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
        <Loader size={22} className="spin" />
        <p style={{ fontSize: '13px', marginTop: '10px' }}>Chargement du ciblage…</p>
      </div>
    );
  }

  /*
   * ⚠️ Sans référentiel, on n'affiche RIEN plutôt que des identifiants techniques.
   * Un libellé inventé — ou une clé brute présentée comme un libellé — se lirait comme une
   * information vérifiée. C'est la leçon des sept noms inventés du repli de `ga4FieldValidator`.
   */
  if (erreurRef || !referentiel) {
    return (
      <div style={{
        display: 'flex', gap: '10px', padding: '18px', borderRadius: '12px',
        backgroundColor: '#fef2f2', border: '1px solid #fecaca'
      }}>
        <AlertCircle size={18} style={{ color: '#dc2626', flexShrink: 0 }} />
        <p style={{ fontSize: '13px', color: '#991b1b', lineHeight: 1.5, margin: 0 }}>
          {erreurRef || "Le référentiel des leviers n'a pas pu être chargé."} L'écran ne peut pas
          nommer les signaux sans lui, et préfère ne rien afficher plutôt que des identifiants
          techniques.
        </p>
      </div>
    );
  }

  const lienExport = `/api/competitors/${concurrent.id}/observations/export.csv`;

  return (
    <div>
      {erreur && (
        <div style={{
          display: 'flex', gap: '8px', padding: '12px 14px', borderRadius: '8px',
          backgroundColor: '#fef2f2', border: '1px solid #fecaca', marginBottom: '16px'
        }}>
          <AlertCircle size={15} style={{ color: '#dc2626', flexShrink: 0, marginTop: '1px' }} />
          <span style={{ fontSize: '12px', color: '#991b1b' }}>{erreur}</span>
        </div>
      )}

      {/* La barre de gestes : consigner un relevé est l'action principale de cet onglet. */}
      {saisieAutorisee && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
          <button onClick={() => setModale({ observation: null })} style={{
            display: 'inline-flex', alignItems: 'center', gap: '7px',
            padding: '9px 16px', borderRadius: '8px', border: 'none',
            backgroundColor: '#3b82f6', color: '#fff', fontSize: '13px', fontWeight: '500', cursor: 'pointer'
          }}>
            <Plus size={15} /> Consigner un relevé
          </button>
        </div>
      )}

      <SyntheseCiblage synthese={synthese} familles={familles} seuils={seuils} />

      {/* La comparaison avec nos propres leviers. Elle n'apparaît qu'une fois lue : un panneau
          vide en attendant ferait croire que nous n'activons rien. */}
      {nos && <NosLeviers nos={nos} synthese={synthese} />}

      <ParcoursProspect
        concurrent={concurrent}
        token={token}
        gestes={gestes}
        parcours={parcours}
        saisieAutorisee={saisieAutorisee}
        onChange={rafraichir}
      />

      {/* Le ciblage publié, rappelé ici : c'est l'autre moitié de la question, et sa carte porte
          la phrase qui empêche de le lire comme un profil d'audience. */}
      {synthese?.ciblageDsa && (
        <div style={{ marginBottom: '20px' }}>
          <VerdictLargeurCiblage ciblage={synthese.ciblageDsa} />
        </div>
      )}

      {/* Le filtre par levier : les pastilles de la synthèse ne sont pas cliquables, le filtre
          est explicite ici — et son libellé vient du référentiel. */}
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '12px' }}>
        <label style={{ fontSize: '12px', color: '#6b7280' }}>Levier</label>
        <select
          value={levier}
          onChange={e => { setLevier(e.target.value); setPage(1); }}
          style={{ padding: '7px 10px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '12px', minWidth: 0 }}
        >
          <option value="">Tous</option>
          {(referentiel.leviers || []).map(l => <option key={l.cle} value={l.cle}>{l.libelle}</option>)}
        </select>
      </div>

      <JournalObservations
        observations={observations}
        total={total}
        page={page}
        totalPages={totalPages}
        tri={tri}
        levier={levier}
        onTri={(t) => { setTri(t); setPage(1); }}
        onPage={setPage}
        saisieAutorisee={saisieAutorisee}
        onCorriger={(o) => setModale({ observation: o })}
        onSupprimer={supprimer}
        lienExport={lienExport}
        libelleSource={libelleSource}
        libelleGeste={libelleGeste}
      />

      {modale && saisieAutorisee && (
        <ModaleObservation
          concurrent={concurrent}
          token={token}
          referentiel={referentiel}
          observation={modale.observation}
          creas={creas}
          onFermer={() => setModale(null)}
          onEnregistre={rafraichir}
        />
      )}
    </div>
  );
};

export default OngletCiblage;
